import get from 'lodash/get';
import { oc } from 'ts-optchain';
import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { Benefits } from './types';

export const guidedSearchTopicsSelector = (state: RootState) =>
  get(state, 'cost.costGuidedSearchTopics', []);
export const userPathSelector = (state: RootState) => get(state, 'cost.costUserPath', {});
export const isFetchingSelector = (state: RootState) => get(state, 'cost.costIsFetching');
export const paginationFetchingSelector = (state: RootState) =>
  get(state, 'cost.paginationFetching');
export const searchResultsSelector = (state: RootState) => get(state, 'cost.costSearchResults');
export const selectedResultSelector = (state: RootState) => get(state, 'cost.costSelectedResult');
export const costProviders = (state: RootState) => state.cost.costProviders;
export const costVisitTypeID = (state: RootState) => get(state, 'cost.costVisitTypeID');
export const providerCostSelector = (state: RootState) => get(state, 'cost.providerCost');
export const patientCostSelector = (state: RootState) => get(state, 'cost.patientCost');
export const termsAndConditions = (state: RootState) =>
  get(state, 'cost.termsAndConditions', false);
export const costProviderDataIsFetchingSelector = (state: RootState) =>
  get(state, 'cost.costProviderDataIsFetching', false);
export const planBenefitsSelector = (state: RootState) =>
  get(state, 'cost.planBenefits', undefined);
export const totalCountOfProvidersSelector = (state: RootState) =>
  get(state, 'cost.totalCountOfProviders', undefined);
export const procedureCostRangesSelector = (state: RootState) => {
  let localAvg = null;
  let nationalAvg = null;
  if (state.cost.procedureCosts) {
    if (get(state, 'cost.procedureCosts.localAverage.minValue')) {
      localAvg = state.cost.procedureCosts.localAverage;
    }
    if (get(state, 'cost.procedureCosts.nationalAverage.minValue')) {
      nationalAvg = state.cost.procedureCosts.nationalAverage;
    }
    if (localAvg || nationalAvg) {
      return { local: localAvg, national: nationalAvg };
    }
  }
  return null;
};

export const costEstimatorSearchTextSelector = (state: RootState) =>
  get(state, 'cost.costEstimatorSearchText');

export const hasAcceptedCostTCSelector = (state: Record<string, any>) => {
  const isConsumerWithValidTerms = oc(state)
    .profile.consumer.externalTermsAcceptance([])
    .find(terms => {
      return terms.type === 'COST_ESTIMATOR';
    });
  const hasTempTerms = oc(state).global.temporaryTCAccept.COST_ESTIMATOR(false);
  return isConsumerWithValidTerms || hasTempTerms;
};
export const providerDetailSelector = (state: Record<string, any>) =>
  get(state, 'cost.costProviderDetails', undefined);

export const myPlanBenefitsSelector = createSelector([planBenefitsSelector], benefits => {
  if (!benefits || !benefits.data) {
    return undefined;
  }

  const values = Object.values(benefits.data);
  if (!values.length) {
    return undefined;
  }
  const lastBenefit = values[values.length - 1] as Benefits;

  return lastBenefit.individual;
});

export const familyPlanBenefitsSelector = createSelector([planBenefitsSelector], benefits => {
  if (!benefits || !benefits.data) {
    return undefined;
  }

  const values = Object.values(benefits.data);
  if (!values.length) {
    return undefined;
  }
  const lastBenefit = values[values.length - 1] as Benefits;

  return lastBenefit.family;
});

export const visitTypeBookableSelector = createSelector([costVisitTypeID], visitTypes => {
  return !!visitTypes.length;
});

export const providerBookableSelector = createSelector([providerDetailSelector], provider => {
  return get(provider, 'isAcceptingOnlineBooking', false);
});
